import { useEffect, useRef, useState } from 'react';
import { Box, Column, InputTextField, Row, Sidebar, Typography } from '@tempmee/react';
import { OutlineSearchIcon } from '@tempmee/react-icons';
import { colors, shadows } from '@tempmee/tailwind';
import { useAppSelector } from '~/hooks';
import ArrowIcon from '~/images/arrow-switch-horizontal.png';
import './OfficeSwitcher.scss';
import { OfficeAvatar } from './OfficeAvatar';
import { getOfficeSwitcherHeaderProps } from './officeSwitcherHeaderProps';

interface IProps {
  onOfficeClicked: (officeId: string | null) => void;
  onSearch: (terms: string) => void;
  onClose: () => void;
}

/**
 * The expanded office-switcher dropdown. Anchored to both edges of its
 * positioned ancestor (the sidebar) so long office names wrap instead of
 * widening the panel over the main dashboard content (GOTU-3780).
 */
export const OfficeSwitcherPanel = ({ onOfficeClicked, onSearch, onClose }: IProps) => {
  const childOffices = useAppSelector((state) => state.user.childOffices);
  const filteredChilds = useAppSelector((state) => state.user.filteredChilds);
  const masterOfficeId = useAppSelector((state) => state.user?.user?.id);
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  const [searchChildOffice, setSearchChildOffice] = useState('');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // The panel only mounts while open, so focusing on mount = focus on open
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleSearchChange = (terms: string) => {
    setSearchChildOffice(terms);
    if (terms) {
      onSearch(terms);
    }
  };

  const listOffices = searchChildOffice !== '' && filteredChilds ? filteredChilds : childOffices;

  const handleOfficeClick = (officeId: string | null) => {
    onClose();
    onOfficeClicked(officeId);
  };

  const renderOfficeList = () => {
    if (listOffices.length === 0) {
      return (
        <Row align="center" justify="center" width="100%" p={3}>
          <Typography variant="body1" bold data-cy="no-offices">
            There are no offices
          </Typography>
        </Row>
      );
    }
    return listOffices
      .filter((o) => o.id !== selectedOfficeId)
      .map((office) => (
        <Box
          key={office.id}
          className="office-switcher-item"
          width="100%"
          style={{ cursor: 'pointer' }}
          onClick={() => handleOfficeClick(office.id)}
        >
          <Row align="center" gap={3} p={2}>
            {/* flexShrink 0: preflight's img max-width:100% squishes the avatar
                when long office names compress this flex cell */}
            <Box style={{ flexShrink: 0 }}>
              <OfficeAvatar
                profileImage={office?.profile_image}
                size="xl"
                showAdminLabel={masterOfficeId === office.id}
              />
            </Box>
            <Column gap={1}>
              {/* body_large_tight = 16px @ line-height 1.2 — the one DS size
                  without the line-height:1 bug, so wrapped names stay legible */}
              <Typography
                variant="body1"
                bold
                color={masterOfficeId === office.id ? colors.primary[500] : undefined}
                data-cy={`office-option-${office?.office_name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              >
                {office.office_name}
              </Typography>
              <Typography size="caption">
                {office.address && office.address.length !== 0
                  ? office.address[0]?.formatted_address
                  : 'N/A'}
              </Typography>
            </Column>
          </Row>
        </Box>
      ));
  };

  return (
    <Column
      // px={4} mirrors the DS Sidebar root's px-4 gutter so the panel's
      // Sidebar.Header lands exactly over the trigger header ("expands in
      // place"); anchoring to the sidebar's top/edges makes long content wrap
      // instead of widening the panel over the dashboard.
      px={4}
      pb={3}
      style={{
        backgroundColor: colors.shades[0],
        position: 'absolute',
        zIndex: 10,
        top: 0,
        left: 0,
        right: 0,
        borderRadius: '10px',
        maxHeight: 500,
        boxShadow: shadows['elevation-4'],
      }}
      ref={wrapperRef}
      data-cy="office-dropdown-menu"
    >
      <Sidebar.Header
        className="office-switcher-header"
        onClick={onClose}
        style={{ cursor: 'pointer' }}
        action={<img src={ArrowIcon} height={24} width={24} alt="close office switcher" />}
        {...getOfficeSwitcherHeaderProps(selectedChildOffice, masterOfficeId)}
      />
      <Box py={3}>
        <InputTextField
          ref={searchInputRef}
          type="text"
          placeholder="Enter office name"
          data-cy="search-office-name"
          onChange={(e) => handleSearchChange(e.target.value)}
          value={searchChildOffice}
          leftIcon={<OutlineSearchIcon color={colors.neutral[400]} />}
        />
      </Box>
      {/* Only the office list scrolls (header + search stay pinned); the
          scrollbar is forced visible via .office-switcher-list so macOS
          overlay scrollbars don't hide that more offices exist */}
      <Column className="office-switcher-list">{renderOfficeList()}</Column>
    </Column>
  );
};
